import React, { useState, useEffect, Suspense } from 'react';
import { ReactComponent as EditIcon } from "../../../assets/images/mydevices/editIcon.svg";
import { ReactComponent as LocationIcon } from "../../../assets/images/mydevices/locationIcon.svg";
import './PrinterDetails.css';
import { useTranslation } from "react-i18next";
import { findValueByKey_From_ArrayOfObj, getPlanTranslation } from '../../../constants/AppMethod';
import { useDispatch, useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { GENUINE, LOCALSESSIONDATAKEYS } from '../../../constants/AppConstants';
import { getLocalSessionData, setLocalSessionData } from '../../../utils/Authentication/handleUserSession';
import { updateEnrolledDeviceReset } from '../../../store/UserEnrolledDeviceDetails/UpdateUserEnrolledDeviceDetailsSlice';
import DeviceConfig from './AddDevice/DeviceConfig';
import Loader from '../../../components/shared/loader/Loader';
import { Modal, ModalHeader } from 'reactstrap';
import { ReactComponent as ModalCloseIcon } from "../../../assets/images/mydevices/modal-close-icon.svg";
import { getUserAddressReset } from '../../../store/GetUserAddress/getUserAddressSlice';
import { GetUserSubscription, ResetUserSubscription } from '../../../actions/Dashboard';
import { DisplayDate } from '../../../utils/DisplayDate';
import { printerTypes } from '../../../constants/PrinterTypes';

const PrinterSection = ({ deviceData, deviceInfoResponse, deviceStatusResponse }) => {
  const { t } = useTranslation(["translation"]);
  const dispatch = useDispatch();
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };
  const editDeviceDetails = () => {
    dispatch(updateEnrolledDeviceReset());
    dispatch(getUserAddressReset());
    toggleModal();
  }

  var warrantyStatus = null;
  var warrantyStatusClassName = null;
  const userId = getLocalSessionData(LOCALSESSIONDATAKEYS.UID);
  const deviceId = deviceData?.deviceId;
  const ecoProService = deviceData?.services?.ecopro;
  const brotherService = deviceData?.services?.brother;

  const isEcoProSuccess = ecoProService?.isSuccess === true;
  const isBrotherSuccess = brotherService?.isSuccess === true;
  const isEcoProCancelled = deviceInfoResponse?.response?.service === null;
  const showEcoInfo = isEcoProSuccess && !isEcoProCancelled;

  const isDeviceEnrolledToBrotherPlusAlone = isBrotherSuccess && !showEcoInfo;
  const isDeviceEnrolledToEcoPro = showEcoInfo;
  const getUserSubscriptionsResponse = useSelector(state => state.GetUserSubscription)
  const getDevicesResponse = useSelector((state => state.BROTHERPLUS.getDevices))
  var subscriptionActivatedDate = null;
  const [isHwInclSubscriptionType, SetIsHwInclSubscriptionType] = useState(false);
  const [hwInclSubscriptionEndDate, setHwInclSubscriptionEndDate] = useState(null);


  useEffect(() => {
    // reset getUserSubscriptions state
    dispatch(ResetUserSubscription());

    // get user subscription start date and end date
    dispatch(GetUserSubscription(userId, deviceId));
  },[userId, deviceId, dispatch])

  useEffect(() => {
    if (getUserSubscriptionsResponse?.success) {
      const userSubscriptionViewModel = getUserSubscriptionsResponse?.userSubscription?.userSubscriptionViewModels[0]
      if (userSubscriptionViewModel?.deviceOption === "purchase") {
        SetIsHwInclSubscriptionType(true);
        setHwInclSubscriptionEndDate(userSubscriptionViewModel?.termEndDate);
        setLocalSessionData(LOCALSESSIONDATAKEYS.TERM_END_DATE, userSubscriptionViewModel?.termEndDate)
      } else {
        SetIsHwInclSubscriptionType(false);
        setHwInclSubscriptionEndDate(null);
      }
    }
    else {
      SetIsHwInclSubscriptionType(false);
      setHwInclSubscriptionEndDate(null);
    }
  }, [getUserSubscriptionsResponse])
  
  const serialNumber = getLocalSessionData(LOCALSESSIONDATAKEYS.SERIAL_NUMBER);
  const printerType = findValueByKey_From_ArrayOfObj(getDevicesResponse?.devices?.devices, 'serialNumber', serialNumber, 'printerType');

  /**
   * This block handles warranty status, in the case where the device is enrolled to Brother Plus alone.
   * If printer type is Monolaser, it checks if the black cartridge is genuine.
   * If printer type is not Monolaser, it checks if all cartridges are genuine.
   */
  if (deviceStatusResponse?.success && isDeviceEnrolledToBrotherPlusAlone) {
    const isGenuine = (printerType === printerTypes.MONO_LASER)
      ? deviceStatusResponse?.response?.genuineInfo?.black?.toLowerCase() === GENUINE
      : deviceStatusResponse?.response?.genuineInfo !== null &&
        Object.values(deviceStatusResponse?.response?.genuineInfo).every(value => value === GENUINE);

    if (isGenuine) {
      warrantyStatus = t("BROTHER_PLUS.DASHBOARD.DEVICE_WARRANTY_STATUS.GENUINE_USER");
      warrantyStatusClassName = 'brother-plus-warranty-status-genuine';
    } else {
      warrantyStatus = t("BROTHER_PLUS.DASHBOARD.DEVICE_WARRANTY_STATUS.NON_GENUINE_USER");
      warrantyStatusClassName = 'brother-plus-warranty-status-non-genuine';
    }
  }

  if (deviceInfoResponse?.success && showEcoInfo) {
    warrantyStatus =
      `${t('DASHBOARD.ENROLLED_DEVICE_SECTION.ECOPRO_SUBSCRIPTION')}, 
      ${getPlanTranslation(deviceInfoResponse?.response?.service?.currentPlan?.planName)} - 
      ${t("DASHBOARD.PAGES_PER_MONTH", { plan_pages: deviceInfoResponse?.response?.service?.currentPlan?.planPages })}`;
    subscriptionActivatedDate = deviceInfoResponse?.response?.service?.activatedDate;
  }

  return (
    <>
      <div className='image-wrapper'>
        <img src={deviceData?.imagePath} alt="Printer" className="printer-image" />
      </div>
      <div className="details-text">
        <div className="title-edit">
          <>
            <span className='device-name'> {deviceData?.deviceName || deviceData?.model}</span>
            <span className="edit-icon" onClick={editDeviceDetails}><EditIcon /></span>
          </>
        </div>
        <div className="location-edit">
          <div className='office-detail-wrapper'>
            <span className="location-icon"><LocationIcon /></span>
            <span className='office-name'>{deviceData?.location ?? ""}</span>
          </div>
        </div>
        <div className='model-wrapper'>
          <p className='device-other-info'><span>{t("MY_DEVICES.DEVICE_DETAILS.MODEL")}</span> {deviceData?.model}</p>
        </div>
        <div className='other-info-wrapper'>
          <p className='device-other-info'><span>{t("MY_DEVICES.DEVICE_DETAILS.SERIAL_NUMBER")}</span> {deviceData?.serialNumber ?? ""}</p>
          {
            (isDeviceEnrolledToEcoPro &&
              ((isHwInclSubscriptionType && hwInclSubscriptionEndDate)
                || (!isHwInclSubscriptionType && subscriptionActivatedDate))) ?
              <p className='device-other-info'>
                <span>{(isHwInclSubscriptionType) ? t('MINIMUM_TERM_PERIOD.MINIMUM_TERM_END_DATE_DASHBOARD') : t('DASHBOARD.ENROLLED_DEVICE_SECTION.START_DATE')}: </span>
                {(getUserSubscriptionsResponse.loading) ? (
                  <Skeleton height="16px" width="180px" duration={0.9} />
                ) : (
                  isHwInclSubscriptionType
                    ? ((hwInclSubscriptionEndDate) ? DisplayDate(hwInclSubscriptionEndDate, { year: 'numeric', month: '2-digit', day: '2-digit' }) : "")
                    : ((subscriptionActivatedDate) ? DisplayDate(subscriptionActivatedDate, { year: 'numeric', month: '2-digit', day: '2-digit' }) : "")
                )}
              </p> : null
          }
          <p className='device-other-info'>
            <span>{t("MY_DEVICES.DEVICE_DETAILS.WARRANTY_STATUS")} </span>
            {(deviceStatusResponse?.loader || deviceInfoResponse?.loader) ? (
              <Skeleton height="16px" width="180px" duration={0.9} />
            ) : (
              <span className={warrantyStatusClassName ?? ''}>{warrantyStatus ?? ''}</span>
            )}
          </p>
          {(isDeviceEnrolledToBrotherPlusAlone) ?
            <p className='device-other-info'><span>{t("BROTHER_PLUS.DASHBOARD.ENROLLED_DEVICE_SECTION.CURRENT_PLAN")}: </span>
              {t('BROTHER_PLUS.DASHBOARD.ENROLLED_DEVICE_SECTION.BROTHER_PLUS_PROGRAM')}
            </p> : null
          }
        </div>
        {/* //following code has been commented temporarily, it will be reused 
        //after final wireframes for multipleddevice feature are ready
        <div className='other-info-wrapper'>
          <p className='device-other-info'><span>{t("MY_DEVICES.DEVICE_DETAILS.WARRANTY_STATUS")}s</span> {deviceData?.status || 'N/A'}</p>
          <p className='device-other-info'><span>{t("MY_DEVICES.DEVICE_DETAILS.PURCHASE_DATE")}</span> {deviceData?.enrolledDate || 'N/A'}</p>
          <p className='device-other-info'><span>{t("MY_DEVICES.DEVICE_DETAILS.EXPIRY_DATE")} dd/mm/yy</span></p>
        </div> */}
      </div>

      <Modal isOpen={modalIsOpen} className="my-devices-add-device-modal-container" fullscreen>
        <ModalHeader close={<ModalCloseIcon onClick={() => toggleModal()} />}></ModalHeader>
        <div className="add-device-modal-content-wrapper">
          <Suspense fallback={<Loader />}>
            <DeviceConfig editDeviceDetail={true} deviceData={deviceData} ></DeviceConfig>
          </Suspense>
        </div>
      </Modal>
    </>
  )
}

const PrinterDetails = ({ deviceData, deviceInfoResponse, deviceStatusResponse }) => {
  return (
    <>
        <div className="printer-details">
          <PrinterSection deviceData={deviceData} deviceInfoResponse={deviceInfoResponse} deviceStatusResponse={deviceStatusResponse} />
        </div>

    </>
  );
};

export default PrinterDetails;